import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";

import { makeStyles } from "@material-ui/core/styles";
import {
    Grid,
    TextField,
    List,
    ListItem,
    ListItemText,
    Typography,
    Divider,
    Paper,
    InputAdornment,
    CircularProgress,
} from "@material-ui/core";

import SearchIcon from "@material-ui/icons/Search";
import OpenWithIcon from "@material-ui/icons/OpenWith";

import { eventCodes } from "../../../../../data-store/actions/registry-actions";
import SimpleDialogDemo from "../../../Modals/SimpleDialog";
import UpdateCode from "./UpdateCode";

const useStyles = makeStyles((theme) => ({
    root: {
        width: "100%",
        padding: "20px",
    },

    search: {
        marginBottom: "20px",
    },

    listContainer: {
        maxHeight: "65vh",
        overflowY: "auto",
        border: "1px solid #ddd",
        borderRadius: "4px",
    },

    listItem: {
        "&:hover": {
            backgroundColor: "#f5f5f5",
        },
    },

    code: {
        fontWeight: "bold",
        fontSize: "18px",
    },

    empty: {
        padding: "30px",
        textAlign: "center",
    },

    counter: {
        marginBottom: "10px",
    },
}));

export default function CodesSearch() {
    const classes = useStyles();
    const dispatch = useDispatch();

    const [search, setSearch] = useState("");

    const eventDetail = useSelector(
        (state) => state.events.eventDetail
    );

    const codesList = useSelector(
        (state) => state.registry.codesList
    );

    const codesGroups = useSelector(
        (state) => state.registry.codesGroups
    );

    // Para eventos con muchos códigos, armar/renderizar la lista completa
    // puede tomar un momento notable — sin esto la pantalla se queda sin
    // feedback (parece colgada) mientras baja la data y se arma la lista.
    const [cargando, setCargando] = useState(true);

    useEffect(() => {
        if (!eventDetail || !eventDetail.iDEvento) return;

        dispatch(
            eventCodes("evento_" + eventDetail.iDEvento)
        );
    }, [dispatch, eventDetail]);

    useEffect(() => {
        if (!codesList) {
            setCargando(true);
            return;
        }
        // Un tick de por medio para que el navegador alcance a pintar el
        // mensaje de "Cargando..." antes de que se arme la lista (ver
        // mismo patrón en CodeReports.jsx).
        setCargando(true);
        const idTimeout = setTimeout(() => setCargando(false), 0);
        return () => clearTimeout(idTimeout);
    }, [codesList]);

    const normalizeString = (value) => {
        if (value === undefined || value === null) {
            return "";
        }

        return String(value)
            .normalize("NFD")
            .replace(/[\u0300-\u036f]/g, "")
            .toLowerCase()
            .trim();
    };

    const codesArray = useMemo(() => {
        if (!codesList) return [];

        return Object.keys(codesList).map((index) => ({
            ...codesList[index],
            index,
        }));
    }, [codesList]);

    const filteredCodes = useMemo(() => {
        const searchValue = normalizeString(search);

        if (!searchValue) {
            return codesArray;
        }

        return codesArray.filter((item) => {
            const searchableText = Object.values(item)
                .filter(
                    (value) =>
                        typeof value === "string" ||
                        typeof value === "number"
                )
                .map((value) => normalizeString(value))
                .join(" ");

            return searchableText.includes(searchValue);
        });
    }, [codesArray, search]);

    const getCodesGroup = (item) => {
        if (!codesGroups || !item) return null;

        // Si el código guarda directamente la llave del grupo
        if (
            item.codeGroupIndex &&
            codesGroups[item.codeGroupIndex]
        ) {
            return codesGroups[item.codeGroupIndex];
        }

        if (
            item.groupIndex &&
            codesGroups[item.groupIndex]
        ) {
            return codesGroups[item.groupIndex];
        }

        if (
            item.codeGroupId &&
            codesGroups[item.codeGroupId]
        ) {
            return codesGroups[item.codeGroupId];
        }

        // Si guarda solamente el nombre del grupo
        if (item.codeGroup) {
            const groupKey = Object.keys(codesGroups).find(
                (index) =>
                    normalizeString(
                        codesGroups[index].codeGroup
                    ) === normalizeString(item.codeGroup)
            );

            if (groupKey) {
                return codesGroups[groupKey];
            }
        }

        return null;
    };

    const getSecondaryText = (item) => {
        const values = [];

        if (item.codeGroup) {
            values.push(`Grupo: ${item.codeGroup}`);
        }

        if (item.typeCode) {
            values.push(`Tipo: ${item.typeCode}`);
        }

        if (
            item.discount !== undefined &&
            item.discount !== null
        ) {
            values.push(`Descuento: ${item.discount}`);
        }

        if (
            item.used !== undefined &&
            item.used !== null
        ) {
            values.push(
                item.used ? "Utilizado" : "Disponible"
            );
        }

        return values.join(" • ");
    };

    return (
        <div className={classes.root}>
            <Grid container spacing={2}>
                <Grid item xs={12}>
                    <Typography variant="h4" gutterBottom>
                        Buscador de códigos
                    </Typography>

                    <Typography
                        variant="body2"
                        color="textSecondary"
                    >
                        Busca códigos de descuento o prepago del evento.
                    </Typography>
                </Grid>

                <Grid item xs={12}>
                    <TextField
                        className={classes.search}
                        variant="outlined"
                        fullWidth
                        autoFocus
                        label="Buscar código"
                        placeholder="Código, grupo, nombre..."
                        value={search}
                        onChange={(e) =>
                            setSearch(e.target.value)
                        }
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <SearchIcon />
                                </InputAdornment>
                            ),
                        }}
                    />
                </Grid>

                {!cargando && (
                    <Grid item xs={12}>
                        <Typography
                            variant="body2"
                            color="textSecondary"
                            className={classes.counter}
                        >
                            {filteredCodes.length} código
                            {filteredCodes.length !== 1 ? "s" : ""}{" "}
                            encontrado
                            {filteredCodes.length !== 1 ? "s" : ""}
                        </Typography>
                    </Grid>
                )}

                <Grid item xs={12}>
                    <Paper
                        variant="outlined"
                        className={classes.listContainer}
                    >
                        {cargando ? (
                            <div className={classes.empty}>
                                <CircularProgress />
                                <Typography
                                    color="textSecondary"
                                    style={{ marginTop: 20 }}
                                >
                                    Cargando códigos del evento...
                                </Typography>
                            </div>
                        ) : filteredCodes.length === 0 ? (
                            <div className={classes.empty}>
                                <Typography color="textSecondary">
                                    No se encontraron códigos.
                                </Typography>
                            </div>
                        ) : (
                            <List disablePadding>
                                {filteredCodes.map((item, index) => {
                                    const codesGroup =
                                        getCodesGroup(item);

                                    const code =
                                        item.code ||
                                        item.codigo ||
                                        item.index;

                                    return (
                                        <React.Fragment key={item.index}>
                                            <ListItem
                                                className={classes.listItem}
                                            >
                                                <ListItemText
                                                    primary={
                                                        <Typography
                                                            className={classes.code}
                                                        >
                                                            {code}
                                                        </Typography>
                                                    }
                                                    secondary={getSecondaryText(
                                                        item
                                                    )}
                                                />

                                                <SimpleDialogDemo
                                                    buttonText="Editar"
                                                    fullScreen={true}
                                                    icon={<OpenWithIcon />}
                                                >
                                                    <UpdateCode
                                                        code={item}
                                                        codesGroup={codesGroup}
                                                    />
                                                </SimpleDialogDemo>
                                            </ListItem>

                                            {index <
                                                filteredCodes.length - 1 && (
                                                    <Divider />
                                                )}
                                        </React.Fragment>
                                    );
                                })}
                            </List>
                        )}
                    </Paper>
                </Grid>
            </Grid>
        </div>
    );
}